import { Column, Entity, Index } from 'typeorm';
import type { CustomFieldSurfacesModel } from '../../constants/custom-field-surfaces';
import type { CustomFieldDataType } from '../types';
import { BaseEntity } from './base.entity';

@Index(
	'idx_custom_field_owner_entity_key',
	['ownerUserId', 'entityType', 'fieldKey'],
	{
		unique: true,
	},
)
@Index('idx_custom_field_entity_active', ['entityType', 'isActive'], {})
@Entity('custom_field_definition')
export class CustomFieldDefinitionEntity extends BaseEntity {
	@Column('varchar', { name: 'fieldKey', length: 60 })
	public fieldKey!: string;

	@Column('varchar', { name: 'entityType', length: 50 })
	public entityType!: string;

	@Column('varchar', { name: 'label', length: 255 })
	public label!: string;

	@Column('varchar', { name: 'fieldType', length: 50 })
	public fieldType!: string;

	@Column('varchar', { name: 'sqlColumnType', length: 100 })
	public sqlColumnType!: string;

	// Value semantics for matching/faceting. `fieldType` stays the UI control;
	// this is what comparators read to decide numeric vs text handling.
	@Column('varchar', { name: 'dataType', length: 16, default: 'text' })
	public dataType!: CustomFieldDataType;

	// Canonical unit for the numeric kinds (e.g. 'mm'). Values are normalized to
	// it on write, so stored numbers are always directly comparable.
	@Column('varchar', { name: 'unit', nullable: true, length: 32 })
	public unit!: string | null;

	@Column('varchar', { name: 'taxonomyKey', nullable: true, length: 64 })
	public taxonomyKey!: string | null;

	@Column('tinyint', { name: 'isIndexed', width: 1, default: () => "'0'" })
	public isIndexed!: boolean;

	@Column('tinyint', { name: 'isActive', width: 1, default: () => "'1'" })
	public isActive!: boolean;

	@Column('text', { name: 'defaultValue', nullable: true })
	public defaultValue!: string | null;

	@Column('tinyint', { name: 'isRequired', width: 1, default: () => "'0'" })
	public isRequired!: boolean;

	@Column('json', {
		name: 'validationRules',
		nullable: true,
		comment: 'JSON object with validation rules (min, max, pattern, etc.)',
	})
	public validationRules!: Record<string, unknown> | null;

	@Column('json', {
		name: 'allowedRoles',
		nullable: true,
		comment: 'Array of role IDs that can see this field. Empty = all roles',
	})
	public allowedRoles!: Array<number> | null;

	@Column('int', { name: 'sortOrder', default: () => "'0'" })
	public sortOrder!: number;

	@Column('json', {
		name: 'selectOptions',
		nullable: true,
		comment: 'Array of options for select/multiselect field types',
	})
	public selectOptions!: Array<{ value: string; label: string }> | null;

	/**
	 * PD-17106 — visit fields only: the rep is asked this field during a visit
	 * debrief (the debrief form, and the Sales Helper when it records a
	 * visit). Ignored for every other entity type.
	 */
	@Column('tinyint', { name: 'askInDebrief', width: 1, default: () => "'0'" })
	public askInDebrief!: boolean;

	/**
	 * PD-17106 — the question as it is asked in the debrief ("Did you discuss
	 * the Emergency Service program?"). Null asks with the field's label.
	 */
	@Column('varchar', { name: 'debriefQuestion', length: 500, nullable: true })
	public debriefQuestion!: string | null;

	// PD-15892 — other screens the field is also filled in on, e.g.
	// `{ "cartSidebar": true }` for an order field shown in the CRM cart
	// sidebar. Null (every existing definition) means only the record's own
	// editor. Validated by describeCustomFieldSurfacesError on write.
	@Column('json', {
		name: 'surfaces',
		nullable: true,
		comment:
			'JSON object of extra screens the field appears on, e.g. {"cartSidebar": true}',
	})
	public surfaces!: CustomFieldSurfacesModel | null;
}
